import { useEffect, useMemo, useRef } from 'react';
import { Platform } from 'react-native';
import type { ItineraryItem } from '@/services/itinerary';
import type { Trip } from '@/services/trips';
import { applePlatformFlags } from '@/config/applePlatformFlags';
import { syncAppleTripSurfaceSnapshot } from '@/services/appleTripSurfaceSync';

const PLAN_SURFACE_SYNC_DEBOUNCE_MS = 1_500;

export type UseAppleTripSurfacePlanSyncOpts = {
  trips: Trip[];
  selectedTrip: Trip | null;
  itineraryItems: ItineraryItem[];
  userId?: string | null;
  /** Skip sync while itinerary is still loading from Firestore. */
  itineraryReady?: boolean;
  /** Only sync when Plan tab is focused (avoids duplicate work with global hook). */
  enabled?: boolean;
};

/**
 * Pushes fresh Plan-tab trip + itinerary data to the App Group snapshot so widgets
 * and Live Activities reflect edits and same-day schedule changes immediately.
 */
export const useAppleTripSurfacePlanSync = (opts: UseAppleTripSurfacePlanSyncOpts): void => {
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestOptsRef = useRef(opts);
  latestOptsRef.current = opts;

  const itinerarySignature = useMemo(
    () =>
      opts.itineraryItems
        .map((item) => {
          const updatedAt =
            item.updatedAt instanceof Date ? item.updatedAt.getTime() : String(item.updatedAt ?? '');
          return `${item.id}:${item.startTime ?? ''}:${item.endTime ?? ''}:${item.status}:${updatedAt}`;
        })
        .join('|'),
    [opts.itineraryItems],
  );

  const flushSync = () => {
    const latest = latestOptsRef.current;
    if (Platform.OS !== 'ios') return;
    if (!applePlatformFlags.phase5Enabled || !applePlatformFlags.widgetNextTripEnabled) return;
    if (!latest.userId || latest.itineraryReady === false) return;
    void syncAppleTripSurfaceSnapshot({
      trips: latest.trips,
      selectedTrip: latest.selectedTrip,
      selectedItineraryItems: latest.itineraryItems,
      userId: latest.userId,
    }).catch(() => {
      // Best-effort surface refresh from Plan tab.
    });
  };

  useEffect(() => {
    if (Platform.OS !== 'ios') return;
    if (!applePlatformFlags.phase5Enabled || !applePlatformFlags.widgetNextTripEnabled) return;
    if (!opts.enabled || !opts.userId || opts.itineraryReady === false) return;

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(flushSync, PLAN_SURFACE_SYNC_DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      if (latestOptsRef.current.enabled) {
        flushSync();
      }
    };
  }, [
    opts.trips,
    opts.selectedTrip?.id,
    opts.userId,
    opts.enabled,
    opts.itineraryReady,
    itinerarySignature,
    opts.itineraryItems,
    opts.selectedTrip,
  ]);
};
